import type { ValueOf } from '@vrplatform/utils';
import { Box } from '@mantine/core';
import type { InputFormSchema } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import {
  CheckInput,
  CurrencyInput,
  DateInput,
  DateInputRange,
  MonthSelect,
  NumberInput,
  Select,
  SelectAutomation,
  SelectData,
  TextInput,
} from './_components';
import type { FormInputValueChangeType } from './_components/_base-input-types';

export const InputByType = ({
  objectKey,
  value,
  onInputValueChange,
}: {
  objectKey: string;
  value: ValueOf<InputFormSchema>;
  onInputValueChange?: FormInputValueChangeType;
}) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const inputByType = useMemo(() => {
    switch (value.type) {
      case 'month':
        return (
          <MonthSelect
            defaultValue={value.defaultValue}
            formKey={objectKey}
            onValueChange={onInputValueChange}
            disabled={value.disabled}
            label={value.label}
            placeholder={value.placeholder}
            required={!value.optional}
          />
        );
      case 'day':
        return (
          <DateInput
            formKey={objectKey}
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            required={!value.optional}
            placeholder={value.placeholder}
            onValueChange={onInputValueChange}
          />
        );
      case 'date':
        return (
          <DateInputRange
            formKey={objectKey}
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            required={!value.optional}
            placeholder={value.placeholder}
            onValueChange={onInputValueChange}
          />
        );
      case 'text':
      case 'string':
      case 'email':
      case 'password':
        return (
          <TextInput
            formKey={objectKey}
            inputType={
              ['text', 'string'].includes(value.type) ? 'text' : value.type
            }
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            required={!value.optional}
            placeholder={value.placeholder}
            onValueChange={onInputValueChange}
          />
        );
      case 'boolean':
        return (
          <CheckInput
            formKey={objectKey}
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            onValueChange={onInputValueChange}
            placeholder={value.placeholder}
            required={!value.optional}
          />
        );

      case 'number':
        return (
          <NumberInput
            formKey={objectKey}
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            required={!value.optional}
            placeholder={value.placeholder}
            onValueChange={onInputValueChange}
          />
        );

      case 'currency':
        return (
          <CurrencyInput
            formKey={objectKey}
            label={value.label}
            defaultValue={value.defaultValue}
            disabled={value.disabled}
            required={!value.optional}
            placeholder={value.placeholder}
            currency={value.currency}
            onValueChange={onInputValueChange}
          />
        );
      case 'select':
        return (
          <Select
            options={value.options}
            label={value.label}
            defaultValue={value.defaultValue}
            formKey={objectKey}
            placeholder={value.placeholder}
            withSearch={value.withSearch}
            required={!value.optional}
            disabled={value.disabled}
            multiple={value.multiple}
            onValueChange={onInputValueChange}
          />
        );
      case 'select-data':
        if (value.sourceType === 'automation')
          return (
            <SelectAutomation
              disabled={value.disabled}
              label={value.label}
              defaultValue={value.defaultValue}
              formKey={objectKey}
              placeholder={value.placeholder}
              required={!value.optional}
              // connectionId={value.connectionId}
              onValueChange={onInputValueChange}
            />
          );
        return (
          <SelectData
            disabled={value.disabled}
            label={value.label}
            defaultValue={value.defaultValue}
            formKey={objectKey}
            placeholder={value.placeholder}
            required={!value.optional}
            sourceType={value.sourceType}
            connectionId={value.connectionId}
            onValueChange={onInputValueChange}
          />
        );

      case 'info':
        return <Box>{value.children}</Box>;

      default:
        return null;
    }
  }, [objectKey, value]);

  return inputByType;
};
